/**
 * Step 02 — where the look comes from (spec §2.2).
 *
 * The lane is a value, not a fork in the code: picking "our defaults" after a
 * read has already happened keeps everything the read found. All this screen
 * decides is whether we ask for a reel next.
 */
import type { Dispatch, RefObject } from "react";
import { Sliders, Sparkles } from "lucide-react";
import type { BrandSetupAction, BrandSetupState } from "../../../lib/brandSetup/state";
import { ChoiceCard, REASON } from "../ui";
import { REASON_ID, useStepReason } from "../StepFrame";

export function PathStep(props: {
  state: BrandSetupState;
  dispatch: Dispatch<BrandSetupAction>;
  /** Wraps the cards so the shell can focus the first one with the reason. */
  cardsRef: RefObject<HTMLDivElement | null>;
}): React.JSX.Element {
  const { state, dispatch, cardsRef } = props;
  const reason = useStepReason();
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div
        ref={cardsRef}
        role="group"
        aria-label="Where your look comes from"
        aria-describedby={reason ? REASON_ID : undefined}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 14,
          borderRadius: 22,
          // The ring the spec asks for when Continue has nothing to work with.
          outline: reason ? "1.5px solid var(--ql-danger)" : "none",
          outlineOffset: 6,
        }}
      >
        <ChoiceCard
          title="Copy from a video"
          copy="Any Instagram, TikTok or YouTube link, or an MP4 / PNG. Yours or one you like."
          icon={<Sparkles size={22} />}
          selected={state.mode === "auto"}
          onClick={() => dispatch({ type: "patch", patch: { mode: "auto" } })}
        />
        <ChoiceCard
          title="Start from our defaults"
          copy="Good, safe choices you can change later"
          icon={<Sliders size={22} />}
          selected={state.mode === "manual"}
          onClick={() => dispatch({ type: "patch", patch: { mode: "manual" } })}
        />
      </div>
      {reason && <div id={REASON_ID} style={REASON}>{reason}</div>}
    </div>
  );
}
